/**
 * Aero Sites — HTML site hosting list page (master).
 *
 * Mounted under AeroLayout, so it reads { user } from the outlet context; the
 * persistent sidebar / auth / subscription handling lives in the layout. Built
 * on the shared Aero / User Testing design components (the same .aero-main
 * shell, cards, buttons, tags) so it sits natively next to User Testing.
 *
 * This is the LIST half of a master-detail pair that mirrors User Testing
 * (UserTestingDashboard → UserTestingProject). It shows only the page header
 * and the grid of site cards plus a New site tile; clicking a card NAVIGATES
 * to that site's detail page (/sites/:siteId, see AeroSiteDetail) where the
 * live preview, deploy area, and version history live.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import firebase from '../../firebase';
import GlassButton from '../../components/Aero/GlassButton';
import AeroPageHeader from '../../components/Aero/AeroPageHeader';
import AeroIcon from '../../components/Aero/AeroIcon';
import { AeroEmptyPanel } from '../../components/Aero/AeroHomeParts';
import AeroStatePanel, { stateFromError, errorLine } from '../../components/Aero/AeroStatePanel';
import AeroCardMenu from '../../components/Aero/AeroCardMenu';
import UnfinishedSites from './UnfinishedSites';
import SiteCard from './SiteCard';
import { testMenuItem, testThisSite } from './testThisSite';
import { discardDraft } from '../../utils/drafts';
import { touchLastActive } from '../../utils/lastActive';
import { buildSiteUrl } from '../../utils/domainUtils';
import { requireServerAnswer } from '../../utils/serverRead';
import { generateReadableSlug, relativeTimeInline, copyActiveVersion } from './sitesUtils';
import '../../styles/userTesting/index.css';
import '../../styles/aero-ut-theme.css';
import './AeroSites.css';

/**
 * Kebab menu for one site card. Maps the site actions onto the shared
 * AeroCardMenu (portalled, ut-theme, click-outside/scroll-close all handled
 * there), so the card grid can never clip it and menu clicks never trigger
 * the card's navigate-on-click.
 */
function SiteCardMenu({ site, busy, canDuplicate = true, onDuplicate, onDelete, onRename, onTest }) {
  const test = onTest ? testMenuItem(site, onTest) : null;
  return (
    <AeroCardMenu
      busy={busy}
      items={[
        ...(test ? [test] : []),
        { label: 'Rename', onClick: () => onRename(site) },
        {
          label: busy ? 'Duplicating…' : 'Duplicate',
          onClick: () => onDuplicate(site),
          disabled: !canDuplicate,
          title: canDuplicate ? undefined : 'Only the owner can duplicate this site',
        },
        { label: 'Delete', onClick: () => onDelete(site), danger: true },
      ]}
    />
  );
}

// What was done on a draft, from the source it was started with.
const DRAFT_WHAT = { drop: 'HTML upload', agent: 'Prompt copied', figma: 'Figma link' };
const draftWhat = (site) => DRAFT_WHAT[site.draftMeta?.source] || 'Started';

function AeroSites() {
  const { user, shell = {}, siteAccess = null } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const db = firebase.firestore();

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  // null, or the AeroStatePanel kind when the list could not load
  const [loadError, setLoadError] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Status filter: null = All, 'live' = live sites, 'offline' = published but
  // not live. Drafts are never in the grid: they sit in the Unfinished row.
  const [statusFilter, setStatusFilter] = useState(null);
  // Search query applied client-side over loaded sites (title / slug / domain).
  const [searchQuery, setSearchQuery] = useState('');

  const fetchSites = useCallback(async () => {
    if (!user?.uid) return;
    try {
      // Only keep html sites plus figma-embed sites created through Aero Sites;
      // the aeroSite flag keeps plain Figmafolio folios off this page.
      // A test copy (sourceSiteId) is a test, listed under Tests, not here.
      const isAeroSiteDoc = (s) => !s.sourceSiteId && (s.type === 'html' || (s.type === 'figma' && s.aeroSite));

      // Owned sites (user/{myUid}/url) plus SHARED sites: a second
      // collection-group query for url docs whose sharedWith array contains my
      // uid (admin "share all sites"). Each site carries its real ownerUid so
      // every mutation can target the owner's path, and shared sites are badged.
      const [ownedSnap, sharedSnap] = await Promise.all([
        db.collection('user').doc(user.uid).collection('url').get().then(requireServerAnswer),
        db.collectionGroup('url').where('sharedWith', 'array-contains', user.uid).get()
          .catch((e) => {
            // A missing collection-group index (manual deploy) or permission
            // issue must not break the owned-sites list.
            console.error('Error loading shared sites:', e);
            return { docs: [] };
          }),
      ]);

      const owned = ownedSnap.docs
        .map((d) => ({ id: d.id, ownerUid: user.uid, shared: false, ...d.data() }))
        .filter(isAeroSiteDoc);
      const shared = sharedSnap.docs
        .map((d) => ({
          id: d.id,
          // The site's REAL owner uid, from the doc path (user/{ownerUid}/url).
          ownerUid: d.ref.parent.parent.id,
          shared: true,
          ...d.data(),
        }))
        // Guard against a doc that also appears in the owned set (shouldn't
        // happen — the callable never shares an owner to themselves — but keep
        // owned authoritative if it ever does).
        .filter((s) => isAeroSiteDoc(s) && s.ownerUid !== user.uid);

      const all = [...owned, ...shared];
      // Heal agent-published drafts: deploy_site set activeVersion server-side
      // but could not clear the client-only draft flag, so a draft published
      // while the flow was closed would stay hidden. A live version means it is
      // no longer a draft. (Client deploys clear draft atomically, so only the
      // agent path reaches here.) Route the heal write to the site's real owner.
      all.forEach((s) => {
        if (s.draft === true && s.activeVersion) {
          s.draft = false;
          db.collection('user').doc(s.ownerUid).collection('url').doc(s.id)
            .set({ draft: firebase.firestore.FieldValue.delete() }, { merge: true })
            .catch((e) => console.error('Draft heal failed:', e));
        }
      });
      setSites(all);
      setLoadError(null);
    } catch (err) {
      console.error('Error loading sites:', err);
      setLoadError(stateFromError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // Throttled "last active = opened the dashboard" stamp on the user doc. The
  // user doc is not loaded on this page (only the url subcollection), so the
  // helper does one get + a conditional write (>24h). Additive, best-effort.
  useEffect(() => {
    if (user?.uid) touchLastActive(user.uid);
  }, [user]);

  // Deep-link create entry: the Aero create gate lands here as
  // /sites?create=agent|convert. New site is a page now (v3, phase 3b), so the
  // link is passed on to /sites/new with the same value (replace, so Back does
  // not bounce). Unknown values are ignored.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const create = params.get('create');
    if (create === 'agent' || create === 'convert') {
      navigate(`/sites/new?create=${create}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // New site is a page (/sites/new) with the same CreateFlow; no url doc is
  // written up front. The flow creates a not-live DRAFT doc when the first
  // source is picked and only commits the real, live site on finish.
  const handleCreateSite = () => navigate('/sites/new');

  // Resume a draft: the same page, restored to the step it was left at.
  const handleResumeDraft = (site) => navigate(`/sites/new?draft=${encodeURIComponent(site.id)}`);

  const startRename = (site) => {
    setRenameTarget(site);
    setRenameValue(site.title || site.generatedUrl);
  };

  const handleRename = async () => {
    if (!user?.uid || !renameTarget || renaming) return;
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    setRenaming(true);
    try {
      // Shared sites write to the OWNER's path, not the logged-in admin's.
      await db
        .collection('user').doc(renameTarget.ownerUid || user.uid)
        .collection('url').doc(renameTarget.id)
        .set({ title: trimmed, updatedAt: new Date() }, { merge: true });
      setRenameTarget(null);
      await fetchSites();
    } catch (err) {
      console.error('Error renaming site:', err);
      toast.error('Could not rename site. ' + errorLine(err));
    } finally {
      setRenaming(false);
    }
  };

  // Duplicate a site: new readable slug, "Copy of" title, and a copy of the
  // active version doc on the duplicate. Blobs are content-addressed and
  // shared per user, so nothing re-uploads. customDomain is never copied.
  //
  // Duplicating creates a NEW url doc, and minting url docs is owner-only in
  // the Firestore rules (a collaborator may edit/delete a shared site but not
  // create docs under the owner's account), so duplicate is offered for OWNED
  // sites only. The menu item is disabled for shared sites; this guard is the
  // belt-and-braces backstop.
  const handleDuplicateSite = async (site) => {
    if (!user?.uid || duplicatingId) return;
    if (site.shared) {
      toast.info('Only the owner can duplicate this shared site.');
      return;
    }
    setDuplicatingId(site.id);
    try {
      const slug = await generateReadableSlug(db);
      const now = new Date();
      const newDoc = {
        userId: user.uid,
        type: 'html',
        // Carry the Aero brand signal so the duplicate is not misclassified
        // as figmafolio. HTML sites use brand:'aero' (matching the create
        // flow); the figma branch below additionally stamps aeroSite:true.
        brand: 'aero',
        generatedUrl: slug,
        title: 'Copy of ' + (site.title || site.generatedUrl),
        createdAt: now,
        updatedAt: now,
      };
      // Figma-embed sites duplicate their embed config instead of versions.
      if (site.type === 'figma') {
        newDoc.type = 'figma';
        newDoc.aeroSite = true;
        if (site.viewports !== undefined) newDoc.viewports = site.viewports;
        if (site.urls !== undefined) newDoc.urls = site.urls;
      }
      if (site.faviconUrl !== undefined) newDoc.faviconUrl = site.faviconUrl;
      if (site.encryptedPassword !== undefined) newDoc.encryptedPassword = site.encryptedPassword;
      if (site.isPasswordActive !== undefined) newDoc.isPasswordActive = site.isPasswordActive;
      if (site.isDraft !== undefined) newDoc.isDraft = site.isDraft;
      await db
        .collection('user').doc(user.uid)
        .collection('url').doc(slug)
        .set(newDoc);
      if (site.activeVersion) {
        await copyActiveVersion(db, user.uid, site.id, site.activeVersion, slug);
      }
      await fetchSites();
      toast.success(
        site.type === 'figma'
          ? `Duplicated to aeroapp.ai/${slug}`
          : `Duplicated to ${slug}.aeroapp.ai`
      );
    } catch (err) {
      console.error('Error duplicating site:', err);
      toast.error('Could not duplicate site. ' + errorLine(err));
    } finally {
      setDuplicatingId(null);
    }
  };

  // Delete the site's version docs (batched) and then the url doc itself.
  // Blobs are NOT touched: they are shared content-addressed storage and
  // cleanup is a future garbage collector's job.
  const handleConfirmDelete = async () => {
    const site = deleteTarget;
    if (!user?.uid || !site || deleting) return;
    setDeleting(true);
    try {
      // A draft discard goes through the shared helper so it also clears any
      // pluginUserTesting bridge doc; a live site delete keeps its original
      // inline version-plus-doc path.
      // Shared sites delete from the OWNER's path, not the logged-in admin's.
      const ownerUid = site.ownerUid || user.uid;
      if (site.draft === true) {
        await discardDraft({ uid: ownerUid, urlId: site.id });
        setDeleteTarget(null);
        await fetchSites();
        toast.info('Draft discarded.');
        return;
      }
      const siteRef = db
        .collection('user').doc(ownerUid)
        .collection('url').doc(site.id);
      const versionsSnap = await siteRef.collection('versions').get();
      const docs = versionsSnap.docs;
      for (let i = 0; i < docs.length; i += 400) {
        const batch = db.batch();
        docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }
      await siteRef.delete();
      setDeleteTarget(null);
      await fetchSites();
      toast.info('Site deleted.');
    } catch (err) {
      console.error('Error deleting site:', err);
      toast.error('Could not delete site. ' + errorLine(err));
    } finally {
      setDeleting(false);
    }
  };

  // Separate drafts from published sites so filter tabs can address each bucket.
  const draftSites = useMemo(() => sites.filter((s) => s.draft === true), [sites]);
  const publishedSites = useMemo(() => sites.filter((s) => s.draft !== true), [sites]);

  // isLive mirrors the card logic: html needs activeVersion, figma needs urls.
  const siteIsLive = (s) =>
    s.type === 'figma'
      ? !!(s.urls && (s.urls.figmaDesktopUrl || s.urls.figmaMobileUrl))
      : !!s.activeVersion;

  const searchQ = searchQuery.trim().toLowerCase();

  // Live bucket filtered by status tab and search.
  const filteredPublished = useMemo(() => {
    let list = publishedSites;
    if (statusFilter === 'live') list = list.filter(siteIsLive);
    if (statusFilter === 'offline') list = list.filter((s) => !siteIsLive(s));
    if (searchQ) {
      list = list.filter(
        (s) =>
          (s.title || '').toLowerCase().includes(searchQ) ||
          (s.generatedUrl || '').toLowerCase().includes(searchQ) ||
          (s.customDomain || '').toLowerCase().includes(searchQ)
      );
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedSites, statusFilter, searchQ]);

  // Draft bucket shown in All and Draft tabs, with search applied.
  const filteredDrafts = useMemo(() => {
    if (!searchQ) return draftSites;
    return draftSites.filter(
      (s) =>
        (s.title || s.generatedUrl || '').toLowerCase().includes(searchQ) ||
        (s.generatedUrl || '').toLowerCase().includes(searchQ)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSites, searchQ]);

  const filterCounts = useMemo(
    () => ({
      all: publishedSites.length,
      live: publishedSites.filter(siteIsLive).length,
      offline: publishedSites.filter((s) => !siteIsLive(s)).length,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sites, publishedSites, draftSites]
  );

  return (
    <div className="aero-main aero-sites-page">
      <Helmet>
        <title>Sites | Aero</title>
        <meta name="description" content="Host static HTML sites on Aero" />
      </Helmet>
      {/* ToastContainer lives INSIDE .aero-main so .aero-main is the single
          flex child of .ut-main (whose `> *` rule sets flex:1 on each child).
          Rendering it as a sibling gives the empty Toastify node its own flex
          row, pushing the page content halfway down. */}
      <ToastContainer position="bottom-right" className="aero-toast-container" />

      <AeroPageHeader
        crumbs="Sites"
        actions={(
          <>
            <GlassButton fill="glass" size="sm" onClick={fetchSites}>
              Refresh
            </GlassButton>
            <GlassButton fill="ink" size="sm" onClick={handleCreateSite}>
              <AeroIcon name="plus" />
              New site
            </GlassButton>
          </>
        )}
      />

      <div className="aero-sites-body">
      {/* Filter tabs + search (mirrors UserTestingDashboard; only shown once sites load) */}
      {!loading && sites.length > 0 && (
        <div className="aero-sites-filter">
          <div className="aero-seg" role="tablist" aria-label="Show">
            {[
              [null, 'All', filterCounts.all],
              ['live', 'Live', filterCounts.live],
              ['offline', 'Offline', filterCounts.offline],
            ].map(([key, label, count]) => (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={statusFilter === key}
                className={`aero-seg-item${statusFilter === key ? ' on' : ''}`}
                onClick={() => setStatusFilter(key)}
              >
                {label}
                <span className="aero-seg-ct">{count}</span>
              </button>
            ))}
          </div>
          <label className="aero-search aero-sites-search">
            <AeroIcon name="search" />
            <input
              type="text"
              placeholder="Search sites"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </label>
        </div>
      )}

      {/* Site list */}
      {loading ? (
        <div className="aero-test-grid aero-scard-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="ut-skeleton" style={{ height: 200, borderRadius: 10 }} />
          ))}
        </div>
      ) : loadError && sites.length === 0 ? (
        <AeroStatePanel
          kind={loadError === 'offline' ? 'offline' : 'error'}
          title={loadError === 'offline' ? undefined : "Your sites didn't load"}
          actions={(
            <GlassButton fill="ink" size="sm" onClick={() => { setLoading(true); fetchSites(); }}>
              Try again
            </GlassButton>
          )}
        />
      ) : sites.length === 0 ? (
        <AeroEmptyPanel
          wash="publish"
          title="Put a site online"
          lead="Drop a folder or .zip, publish from your AI tool, or paste a Figma link. It goes live on its own aeroapp.ai address."
        >
          <GlassButton fill="ink" size="md" onClick={handleCreateSite}>
            <AeroIcon name="plus" />
            New site
          </GlassButton>
        </AeroEmptyPanel>
      ) : (filteredPublished.length === 0 && filteredDrafts.length === 0 && publishedSites.length > 0) ? (
        <AeroStatePanel kind="empty" icon="search" title="No sites here" compact>
          {searchQuery
            ? `Nothing matches “${searchQuery}”. Try another name or address.`
            : statusFilter === 'offline' ? 'None of your sites are offline.' : 'None of your sites are live right now.'}
        </AeroStatePanel>
      ) : (
        <>
        <UnfinishedSites
          drafts={filteredDrafts.map((site) => ({
            id: site.id,
            name: site.title || site.generatedUrl,
            what: draftWhat(site),
            when: relativeTimeInline(site.updatedAt || site.createdAt),
            site,
          }))}
          onResume={(d) => handleResumeDraft(d.site)}
          onDiscard={(d) => setDeleteTarget(d.site)}
        />
        <div className="aero-test-grid aero-scard-grid">
          {filteredPublished.map((site) => {
            // buildSiteUrl prefers a connected custom domain for figma sites
            // and keeps the subdomain for html; brand 'aero' pins the
            // aeroapp.ai host.
            const host = buildSiteUrl({
              generatedUrl: site.generatedUrl,
              type: site.type,
              customDomain: site.customDomain,
              brand: 'aero',
            }).replace(/^https:\/\//, '');
            const isLive = siteIsLive(site);
            return (
              <SiteCard
                key={site.id}
                site={site}
                host={host}
                isLive={isLive}
                access={site.shared ? null : siteAccess}
                updatedLabel={relativeTimeInline(site.updatedAt || site.createdAt)}
                duplicating={duplicatingId === site.id}
                onOpen={() => navigate(`/sites/${site.id}`)}
                rename={renameTarget && renameTarget.id === site.id ? {
                  value: renameValue,
                  busy: renaming,
                  onChange: setRenameValue,
                  onSave: handleRename,
                  onCancel: () => setRenameTarget(null),
                } : null}
                menu={(
                  <SiteCardMenu
                    site={site}
                    busy={duplicatingId === site.id}
                    canDuplicate={!site.shared}
                    onDuplicate={handleDuplicateSite}
                    onDelete={setDeleteTarget}
                    onRename={startRename}
                    onTest={(s) => testThisSite({ uid: user?.uid, site: s, host, navigate, refresh: shell.refresh })
                      .catch((err) => { console.error('Could not start the test:', err); toast.error('Could not start the test. ' + errorLine(err)); })}
                  />
                )}
              />
            );
          })}
          <button
            type="button"
            className="aero-scard-new aero-w-publish"
            onClick={handleCreateSite}
          >
            <AeroIcon name="up" />
            <b>New site</b>
            <span>A folder or zip, a prompt for your AI tool, or a Figma link.</span>
          </button>
        </div>
        </>
      )}

      </div>

      {/* Rename happens in place on the site block (SiteCard's rename prop). */}

      {/* Delete confirm modal (portal, aero glass styling, modelled on the
          UT delete modal pattern). */}
      {deleteTarget &&
        ReactDOM.createPortal(
          <div
            className="ut-modal-root aero-modal-backdrop ut-theme"
            onClick={() => !deleting && setDeleteTarget(null)}
          >
            <div className="aero-modal" onClick={(e) => e.stopPropagation()}>
              <div className="aero-modal-head">
                <h2>
                  {deleteTarget.draft ? 'Discard' : 'Delete'}{' '}
                  {deleteTarget.title || deleteTarget.generatedUrl}?
                </h2>
                <button
                  type="button"
                  className="aero-btn aero-btn-ghost aero-btn-icon"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="aero-modal-body">
                <p style={{ margin: 0 }}>
                  {deleteTarget.draft
                    ? 'This in-progress draft will be removed. This cannot be undone.'
                    : `Delete ${deleteTarget.title || deleteTarget.generatedUrl}? Its versions and history will be removed. This cannot be undone.`}
                </p>
              </div>
              <div className="aero-modal-foot">
                <button
                  type="button"
                  className="aero-btn aero-btn-secondary"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="aero-btn aero-btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting
                    ? (deleteTarget.draft ? 'Discarding…' : 'Deleting…')
                    : (deleteTarget.draft ? 'Discard draft' : 'Delete site')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

    </div>
  );
}

export default AeroSites;
